import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAIAnalyticsAccess } from '../../hooks/useAIAnalyticsAccess';
import { useInventoryAccess } from '../../hooks/useInventoryAccess';
import {
  ChevronLeft,
  ChevronRight,
  Bell,
  Users,
  UsersRound,
  Clock,
  Calendar,
  ClipboardList,
  BarChart3,
  Store,
  Heart,
  Package,
  CreditCard,
  Scissors,
  UserCircle,
  LogOut,
  Home,
  Search,
  X,
  Banknote,
  QrCode,
  Trophy,
  Gift,
  MessageCircle,
  Megaphone,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  path?: string;
  badge?: number;
  onClick?: () => void;
  children?: NavItem[];
}

interface NavSection {
  id: string;
  title?: string;
  items: NavItem[];
}

const Sidebar: React.FC = () => {
  const { state, logout } = useAuth();
  const { isCollapsed, toggleCollapse, isMobileOpen, closeMobile } = useSidebar();
  const location = useLocation();
  const hasAIAnalytics = useAIAnalyticsAccess();
  const hasInventory = useInventoryAccess();
  const [expandedSections, setExpandedSections] = useState<string[]>(['main', 'operations', 'analytics', 'admin-analytics', 'customer']);

  // Helper function to check user role (handles both string and array formats)
  const hasRole = (roleToCheck: 'Customer' | 'ShopOwner' | 'Admin' | 'Barber'): boolean => {
    if (!state.user?.role) return false;
    const userRole = state.user.role;
    if (Array.isArray(userRole)) {
      return userRole.includes(roleToCheck);
    }
    return userRole === roleToCheck;
  };

  // Navigation items based on user role
  const navigationSections = useMemo((): NavSection[] => {
    // Use function to check roles properly for array handling
    const checkRole = (roleToCheck: 'Customer' | 'ShopOwner' | 'Admin' | 'Barber'): boolean => {
      if (!state.user?.role) return false;
      const userRole = state.user.role;
      if (Array.isArray(userRole)) {
        return userRole.includes(roleToCheck);
      }
      return userRole === roleToCheck;
    };
    const isAuthenticated = state.isAuthenticated;

    // Base sections for all users
    const sections: NavSection[] = [];

    // Main navigation section
    const mainSection: NavSection = {
      id: 'main',
      items: [],
    };

    if (isAuthenticated) {
      // Dashboard for authenticated users
      mainSection.items.push({
        id: 'dashboard',
        label: 'Dashboard',
        icon: Home,
        path: checkRole('Admin') ? '/admin/dashboard' : '/dashboard',
      });

      // Notifications
      mainSection.items.push({
        id: 'notifications',
        label: 'Notifications',
        icon: Bell,
        path: '/notifications',
      });

      // Messages (for customers and barbers)
      if (checkRole('Customer') || checkRole('Barber')) {
        mainSection.items.push({
          id: 'messages',
          label: 'Messages',
          icon: MessageCircle,
          path: '/messages',
        });
      }

      // Broadcasts (for barbers only)
      if (checkRole('Barber')) {
        mainSection.items.push({
          id: 'broadcasts',
          label: 'Broadcasts',
          icon: Megaphone,
          path: '/barber/broadcasts',
        });
      }
    }

    // Role-specific main items
    if (checkRole('Customer')) {
      mainSection.items.push(
        {
          id: 'barbershops',
          label: 'Browse Barbershops',
          icon: Search,
          path: '/barbershops',
        },
        {
          id: 'favorites',
          label: 'Favorites',
          icon: Heart,
          path: '/favorites',
        },
        {
          id: 'bookings',
          label: 'My Bookings',
          icon: Calendar,
          path: '/bookings',
        },
        {
          id: 'gift-cards',
          label: 'Gift Cards',
          icon: Gift,
          path: '/gift-cards',
        }
      );
    }

    if (checkRole('Barber')) {
      mainSection.items.push(
        {
          id: 'clients',
          label: 'Clients',
          icon: Users,
          path: '/barber/clients',
        },
        {
          id: 'issued-offers',
          label: 'Issued Offers',
          icon: Gift,
          path: '/barber/issued-offers',
        },
        {
          id: 'calendar',
          label: 'Schedule',
          icon: Calendar,
          path: '/barber/calendar',
        }
      );
    }

    if (checkRole('ShopOwner')) {
      mainSection.items.push(
        {
          id: 'barbershops',
          label: 'My Barbershops',
          icon: Store,
          path: '/shop-owner/barbershops',
        }
      );
    }

    if (checkRole('Admin')) {
      mainSection.items.push(
        {
          id: 'users',
          label: 'Users',
          icon: Users,
          path: '/admin/users',
        },
        {
          id: 'approvals',
          label: 'Approvals',
          icon: ClipboardList,
          path: '/admin/approvals',
        }
      );
    }

    sections.push(mainSection);


    // Operations section (for barbers and shop owners)
    if (checkRole('Barber') || checkRole('ShopOwner')) {
      const operationsSection: NavSection = {
        id: 'operations',
        title: 'Operations',
        items: [],
      };

      if (checkRole('Barber')) {
        operationsSection.items.push(
          {
            id: 'services',
            label: 'Services',
            icon: Scissors,
            path: '/barber/services',
          },
          {
            id: 'locations',
            label: 'Locations',
            icon: Store,
            path: '/barber/barbershops',
          }
        );

        // Inventory is gated by subscription add-on
        if (hasInventory) {
          operationsSection.items.push({
            id: 'inventory',
            label: 'Inventory',
            icon: Package,
            path: '/barber/inventory',
          });
        }

        operationsSection.items.push(
          {
            id: 'check-in-qr',
            label: 'Check-In QR Code',
            icon: QrCode,
            path: '/barber/check-in-qr',
          },
          {
            id: 'membership-plans',
            label: 'Membership Plans',
            icon: CreditCard,
            path: '/barber/membership-plans-manage',
          },
          {
            id: 'payouts',
            label: 'Payouts',
            icon: Banknote,
            path: '/barber/payouts',
          },
          {
            id: 'loyalty-program',
            label: 'Loyalty Program',
            icon: Trophy,
            path: '/barber/loyalty-program',
          },
          {
            id: 'promotions',
            label: 'Promotions',
            icon: Gift,
            path: '/barber/promotions',
          },
          {
            id: 'campaigns',
            label: 'Campaigns',
            icon: Megaphone,
            path: '/barber/campaigns',
          }
        );
      }

      if (checkRole('ShopOwner')) {
        operationsSection.items.push({
          id: 'invitations',
          label: 'Barber Invitations',
          icon: UsersRound,
          path: '/shop-owner/invitations',
        });

        // Inventory is gated by subscription add-on
        if (hasInventory) {
          operationsSection.items.push({
            id: 'inventory',
            label: 'Inventory',
            icon: Package,
            path: '/shop-owner/inventory',
          });
        }

        operationsSection.items.push(
          {
            id: 'check-in-qr',
            label: 'Check-In QR Code',
            icon: QrCode,
            path: '/shop-owner/check-in-qr',
          },
          {
            id: 'loyalty-program',
            label: 'Loyalty Program',
            icon: Trophy,
            path: '/barber/loyalty-program',
          },
          {
            id: 'promotions',
            label: 'Promotions',
            icon: Gift,
            path: '/barber/promotions',
          },
          {
            id: 'campaigns',
            label: 'Campaigns',
            icon: Megaphone,
            path: '/barber/campaigns',
          }
        );
      }

      sections.push(operationsSection);
    }

    // Analytics section (for users with AI analytics access)
    if (hasAIAnalytics && (checkRole('Barber') || checkRole('ShopOwner'))) {
      const analyticsSection: NavSection = {
        id: 'analytics',
        title: 'Analytics',
        items: [],
      };

      if (checkRole('Barber')) {
        analyticsSection.items.push(
          {
            id: 'barber-analytics',
            label: 'Dashboard',
            icon: BarChart3,
            path: '/barber/analytics',
          },
          {
            id: 'revenue-forecast',
            label: 'Revenue Forecast',
            icon: BarChart3,
            path: '/shop-owner/revenue-forecast',
          }
        );
      }

      if (checkRole('ShopOwner')) {
        analyticsSection.items.push(
          {
            id: 'barber-performance',
            label: 'Barber Analytics',
            icon: BarChart3,
            path: '/analytics/barbers',
          },
          {
            id: 'booking-analytics',
            label: 'Booking Analytics',
            icon: BarChart3,
            path: '/analytics/bookings',
          },
          {
            id: 'revenue-forecast',
            label: 'Revenue Forecast',
            icon: BarChart3,
            path: '/shop-owner/revenue-forecast',
          }
        );
      }

      sections.push(analyticsSection);
    }

    // Admin Analytics section
    if (checkRole('Admin')) {
      const adminAnalyticsSection: NavSection = {
        id: 'admin-analytics',
        title: 'Analytics',
        items: [
          {
            id: 'admin-analytics-dashboard',
            label: 'Analytics',
            icon: BarChart3,
            path: '/admin/analytics',
          },
          {
            id: 'usage-analytics',
            label: 'Usage Analytics',
            icon: BarChart3,
            path: '/admin/usage',
          },
          {
            id: 'no-shows',
            label: 'No-Show Management',
            icon: ClipboardList,
            path: '/admin/no-shows',
          },
          {
            id: 'promo-codes',
            label: 'Promo Codes',
            icon: CreditCard,
            path: '/admin/promo-codes',
          },
        ],
      };

      sections.push(adminAnalyticsSection);
    }

    // Customer-specific sections
    if (checkRole('Customer')) {
      const customerSection: NavSection = {
        id: 'customer',
        title: 'My Account',
        items: [
          {
            id: 'waitlist',
            label: 'My Waitlists',
            icon: Clock,
            path: '/waitlist',
          },
          {
            id: 'recurring',
            label: 'Recurring Appointments',
            icon: Calendar,
            path: '/recurring-appointments',
          },
          {
            id: 'memberships',
            label: 'Memberships',
            icon: CreditCard,
            path: '/memberships/dashboard',
          },
          {
            id: 'referrals',
            label: 'Referrals',
            icon: UsersRound,
            path: '/referrals',
          },
        ],
      };

      sections.push(customerSection);
    }

    return sections;
  }, [state.user?.role, state.isAuthenticated, hasAIAnalytics, hasInventory]);

  const isActive = (path: string) => {
    return location.pathname === path ||
           (path !== '/' && location.pathname.startsWith(path));
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleLogout = async () => {
    await logout();
    closeMobile();
  };

  // Get theme colors based on user role
  const getThemeColors = () => {
    if (hasRole('ShopOwner')) {
      return {
        activeBg: 'bg-shopowner-50 dark:bg-shopowner-900/20',
        activeText: 'text-shopowner-600 dark:text-shopowner-400',
        badgeBg: 'bg-shopowner-500',
        hoverBg: 'hover:bg-shopowner-50 dark:hover:bg-shopowner-900/10',
      };
    }
    // Default primary theme (blue) for other roles
    return {
      activeBg: 'bg-primary-50 dark:bg-primary-900/20',
      activeText: 'text-primary-600 dark:text-primary-400',
      badgeBg: 'bg-primary-500',
      hoverBg: 'hover:bg-gray-100 dark:hover:bg-gray-800',
    };
  };

  const themeColors = getThemeColors();

  const renderNavItem = (item: NavItem, depth: number = 0) => {
    const Icon = item.icon;
    const active = item.path ? isActive(item.path) : false;
    const paddingLeft = isCollapsed ? 'px-3' : `pl-${3 + depth * 3}`;

    if (item.onClick && !item.path) {
      return (
        <button
          key={item.id}
          onClick={() => {
            item.onClick?.();
            closeMobile();
          }}
          className={`
            w-full flex items-center gap-3 py-2.5 ${paddingLeft} pr-3 rounded-lg
            text-sm font-medium transition-all duration-200
            text-gray-600 dark:text-gray-400
            hover:bg-gray-100 dark:hover:bg-gray-800
            hover:text-gray-900 dark:hover:text-gray-200
          `}
          title={isCollapsed ? item.label : undefined}
        >
          <Icon className="flex-shrink-0" size={20} />
          {!isCollapsed && (
            <span className="truncate">{item.label}</span>
          )}
          {!isCollapsed && item.badge !== undefined && (
            <span className={`ml-auto ${themeColors.badgeBg} text-white text-xs font-semibold px-2 py-0.5 rounded-full`}>
              {item.badge}
            </span>
          )}
        </button>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path || '/'}
        onClick={closeMobile}
        className={`
          flex items-center gap-3 py-2.5 ${paddingLeft} pr-3 rounded-lg
          text-sm font-medium transition-all duration-200
          ${active
            ? `${themeColors.activeBg} ${themeColors.activeText}`
            : `text-gray-600 dark:text-gray-400 ${themeColors.hoverBg} hover:text-gray-900 dark:hover:text-gray-200`
          }
        `}
        title={isCollapsed ? item.label : undefined}
      >
        <Icon className="flex-shrink-0" size={20} />
        {!isCollapsed && (
          <>
            <span className="truncate">{item.label}</span>
            {item.badge !== undefined && (
              <span className={`ml-auto ${themeColors.badgeBg} text-white text-xs font-semibold px-2 py-0.5 rounded-full`}>
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
    );
  };

  const renderSection = (section: NavSection) => {
    const isExpanded = expandedSections.includes(section.id);

    return (
      <div key={section.id} className="mb-4">
        {section.title && !isCollapsed && (
          <button
            onClick={() => toggleSection(section.id)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <span>{section.title}</span>
            <ChevronRight
              size={14}
              className={`transform transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`}
            />
          </button>
        )}

        {(isExpanded || isCollapsed || !section.title) && (
          <nav className="space-y-1">
            {section.items.map(item => renderNavItem(item))}
          </nav>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <>
      {/* Logo and collapse toggle */}
      <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-4 border-b border-gray-200 dark:border-gray-700`}>
        <Link to="/" className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'}`} onClick={closeMobile}>
          <img
            src="/BarbrIQ-logo.jpeg"
            alt="BarbrIQ Logo"
            className={`${isCollapsed ? 'h-8 w-8' : 'h-10'} w-auto object-contain`}
          />
          {!isCollapsed && (
            <span className={`text-lg font-bold ${hasRole('ShopOwner') ? 'text-shopowner-600 dark:text-shopowner-400' : 'text-primary-600 dark:text-primary-400'}`}>
              BarbrIQ
            </span>
          )}
        </Link>

        {/* Desktop collapse/expand button */}
        <button
          onClick={toggleCollapse}
          className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>

        {/* Mobile close button */}
        <button
          onClick={closeMobile}
          className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Close sidebar"
        >
          <X size={20} />
        </button>
      </div>

      {/* Navigation sections */}
      <div className="flex-1 overflow-y-auto py-4 px-2">
        {navigationSections.map(section => renderSection(section))}

      </div>

      {/* User section at bottom */}
      {state.isAuthenticated && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-2">
          <Link
            to={
              hasRole('Barber')
                ? '/barber/profile'
                : hasRole('ShopOwner')
                ? '/shop-owner/subscription'
                : hasRole('Admin')
                ? '/admin/dashboard'
                : '/profile'
            }
            onClick={closeMobile}
            className={`
              flex items-center gap-3 py-2.5 px-3 rounded-lg
              text-sm font-medium transition-colors
              text-gray-600 dark:text-gray-400
              hover:bg-gray-100 dark:hover:bg-gray-800
              hover:text-gray-900 dark:hover:text-gray-200
            `}
            title={isCollapsed ? 'Profile' : undefined}
          >
            <UserCircle size={20} className="flex-shrink-0" />
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium text-gray-900 dark:text-gray-100">
                  {state.user?.firstName} {state.user?.lastName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {state.user?.email}
                </p>
              </div>
            )}
          </Link>

          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center gap-3 py-2.5 px-3 rounded-lg
              text-sm font-medium transition-colors
              text-danger-600 dark:text-danger-400
              hover:bg-danger-50 dark:hover:bg-danger-900/20
            `}
            title={isCollapsed ? 'Logout' : undefined}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      )}

    </>
  );

  // Only render sidebar when user is authenticated
  if (!state.isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* Desktop sidebar */}
      <aside
        className={`
          hidden lg:flex lg:flex-col
          fixed top-0 left-0 h-screen z-30
          bg-slate-50 dark:bg-gray-900
          border-r border-gray-200 dark:border-gray-700
          transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        {sidebarContent}
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`
          lg:hidden flex flex-col
          fixed top-0 left-0 h-screen z-50
          bg-slate-50 dark:bg-gray-900
          border-r border-gray-200 dark:border-gray-700
          transition-transform duration-300 ease-in-out
          w-72
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;
